import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { withStyles } from "material-ui/styles";
import Grid from "material-ui/Grid";
import IconButton from "material-ui/IconButton";
import Card from "material-ui/Card";
import Drawer from "material-ui/Drawer";
import Slider from "react-slick";
import { DISHES } from "./DishImageGallery.helpers";
import { DrawerHeader } from "./DrawerHeader";
import ArrowForward from "material-ui-icons/ArrowForward";
import ArrowBack from "material-ui-icons/ArrowBack";

const SLIDES_TO_SHOW = 2;
const SLIDES_TO_SCROLL = 2;

const styles = (theme) => ({
  root: {},
  slide: {
    position: "relative",
  },
  sliderWrapper: {
    position: "relative",
  },
  image: {
    objectFit: "cover",
    width: "100%",
    height: "100%",
  },
  label: {
    position: "absolute",
    bottom: 10,
    right: 7,
    color: "#ffffff",
    opacity: 0.8,
    fontSize: "12px",
  },
  navigationButton: {
    position: "absolute",
    top: "50%",
    zIndex: 2,
    transform: "translateY(-50%)",
    width: 36,
    height: 36,
    padding: 6,
    backgroundColor: "rgba(0, 0, 0, 0.55)",
    color: "#ffffff",
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.7)",
    },
  },
  navigationLeft: {
    left: 8,
  },
  navigationRight: {
    right: 8,
  },
});

const DishImageGallery = ({ classes }) => {
  const [open, setOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(DISHES[0].path);
  const [currentLabel, setCurrentLabel] = useState(DISHES[0].label);
  const [dragging, setDragging] = useState(false);
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true);
  const sliderRef = useRef(null);

  const disableAutoScroll = useCallback(() => {
    setAutoScrollEnabled(false);
  }, []);

  const settings = useMemo(
    () => ({
      infinite: true,
      speed: autoScrollEnabled ? 2400 : 500,
      slidesToShow: SLIDES_TO_SHOW,
      slidesToScroll: SLIDES_TO_SCROLL,
      adaptiveHeight: true,
      arrows: false,
      autoplay: autoScrollEnabled,
      autoplaySpeed: 3200,
      pauseOnHover: false,
      pauseOnFocus: false,
    }),
    [autoScrollEnabled]
  );

  const handleBeforeChange = useCallback((current, next) => {
    if (current !== next) {
      setDragging(true);
    }
  }, []);

  const handleAfterChange = useCallback(() => {
    setDragging(false);
  }, []);

  const onClick = useCallback(
    (e, path) => {
      if (dragging) {
        e.stopPropagation();
        return;
      }
      disableAutoScroll();
      setCurrentImage(path);
      setCurrentLabel(DISHES.find((dish) => dish.path === path).label);
      setOpen(true);
    },
    [disableAutoScroll, dragging]
  );

  const goToPreviousSlide = useCallback(() => {
    disableAutoScroll();
    if (sliderRef.current) {
      sliderRef.current.slickPrev();
    }
  }, [disableAutoScroll]);

  const goToNextSlide = useCallback(() => {
    disableAutoScroll();
    if (sliderRef.current) {
      sliderRef.current.slickNext();
    }
  }, [disableAutoScroll]);

  return (
    <Grid style={{ marginTop: "8px" }} item xs={12} md={8}>
      <Card className={classes.card}>
        <div
          className={classes.sliderWrapper}
          onMouseDown={disableAutoScroll}
          onTouchStart={disableAutoScroll}
        >
          <IconButton
            className={`${classes.navigationButton} ${classes.navigationLeft}`}
            onClick={goToPreviousSlide}
            aria-label="Voir les plats précédents"
          >
            <ArrowBack />
          </IconButton>
          <IconButton
            className={`${classes.navigationButton} ${classes.navigationRight}`}
            onClick={goToNextSlide}
            aria-label="Voir les plats suivants"
          >
            <ArrowForward />
          </IconButton>
          <Slider
            ref={sliderRef}
            {...settings}
            beforeChange={handleBeforeChange}
            afterChange={handleAfterChange}
          >
            {DISHES.map(({ path, label }) => (
              <div
                key={path}
                className={classes.slide}
                onClick={(e) => onClick(e, path)}
              >
                <div
                  className={classes.label}
                  style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    backgroundColor: "black",
                    height: 25,
                    textAlign: "center",
                    boxSizing: "border-box",
                    paddingLeft: 16,
                    paddingRight: 16,
                    paddingBottom: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    opacity: "0.6",
                  }}
                >
                  <span>{label}</span>
                </div>
                <img src={path} alt={label} className={classes.image} />
              </div>
            ))}
          </Slider>
        </div>
      </Card>
      <Drawer
        open={open}
        anchor={"top"}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: "white",
            height: "100%",
          },
        }}
      >
        <DrawerHeader onClose={() => setOpen(false)}>
          {currentLabel}
        </DrawerHeader>
        <img src={currentImage} alt={currentLabel} />
      </Drawer>
    </Grid>
  );
};

export default withStyles(styles, { withTheme: true })(DishImageGallery);



// WEBPACK FOOTER //
// ./src/components/DishImageGallery/DishImageGallery.js